import React, { useRef } from "react";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";
import "./RelatorioPlanoComunicacaoPrint.css";

const ID_QUADRO_IMPRESSAO = "quadro-impressao-plano-comunicacao";
const LARGURAS_COLUNAS = ["19%", "21%", "20%", "24%", "16%"];

const ESTILOS_IMPRESSAO_PLANO_COMUNICACAO = `
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #071b31; font-family: Arial, sans-serif; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .pagina-pdf-plano-comunicacao { width: 100%; }
  .cabecalho-pdf-plano-comunicacao, .tabela-pdf-plano-comunicacao { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .cabecalho-pdf-plano-comunicacao td { border: 1px solid #153d64; padding: 6px; text-align: center; }
  .cabecalho-pdf-plano-comunicacao td:first-child { width: 28mm; }
  .cabecalho-pdf-plano-comunicacao td:last-child { width: 40mm; }
  .cabecalho-pdf-plano-comunicacao img { max-width: 26mm; max-height: 15mm; }
  .cabecalho-pdf-plano-comunicacao strong, .cabecalho-pdf-plano-comunicacao span { display: block; }
  .cabecalho-pdf-plano-comunicacao td:nth-child(2) strong { color: #0b4b78; font-size: 16px; }
  .cabecalho-pdf-plano-comunicacao span { margin-top: 3px; font-size: 12px; }
  .objetivo-pdf-plano-comunicacao { margin: 4mm 0; padding: 3mm; border: 1px solid #8da1b5; font-size: 12px; line-height: 1.35; white-space: pre-line; }
  .objetivo-pdf-plano-comunicacao strong { display: block; margin-bottom: 2mm; color: #0b4774; font-size: 12px; }
  .tabela-pdf-plano-comunicacao { font-size: 12px; }
  .tabela-pdf-plano-comunicacao th, .tabela-pdf-plano-comunicacao td { border: 1px solid #777; padding: 4px 3px; text-align: center; vertical-align: middle; overflow-wrap: anywhere; white-space: pre-line; }
  .tabela-pdf-plano-comunicacao th { background: #0b4774; color: #fff; font-size: 12px; }
  .tabela-pdf-plano-comunicacao thead { display: table-header-group; }
  .tabela-pdf-plano-comunicacao tr { break-inside: avoid; page-break-inside: avoid; }
  .tabela-pdf-plano-comunicacao tbody tr:nth-child(even) { background: #f4f7f9; }
`;

const valor = (conteudo) => String(conteudo ?? "").trim() || "-";

function formatarData(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

function imprimirPlanoComunicacao(elemento, nomeArquivo) {
  if (!elemento) return;

  document.getElementById(ID_QUADRO_IMPRESSAO)?.remove();
  const quadroImpressao = document.createElement("iframe");
  quadroImpressao.id = ID_QUADRO_IMPRESSAO;
  quadroImpressao.title = "Impressão do Plano de Comunicação";
  Object.assign(quadroImpressao.style, { position: "fixed", right: "0", bottom: "0", width: "1px", height: "1px", border: "0", opacity: "0", pointerEvents: "none" });
  document.body.appendChild(quadroImpressao);

  const janelaImpressao = quadroImpressao.contentWindow;
  const documentoImpressao = quadroImpressao.contentDocument;
  documentoImpressao.open();
  documentoImpressao.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${nomeArquivo}</title><style>${ESTILOS_IMPRESSAO_PLANO_COMUNICACAO}</style></head><body></body></html>`);
  documentoImpressao.close();
  documentoImpressao.body.appendChild(elemento.cloneNode(true));

  const imagens = Array.from(documentoImpressao.images);
  Promise.all(imagens.map((imagem) => imagem.complete ? Promise.resolve() : new Promise((resolve) => {
    imagem.addEventListener("load", resolve, { once: true });
    imagem.addEventListener("error", resolve, { once: true });
  }))).then(() => {
    janelaImpressao.onafterprint = () => quadroImpressao.remove();
    janelaImpressao.focus();
    janelaImpressao.print();
  });
}

export default function RelatorioPlanoComunicacaoPrint({ plano, onVoltar }) {
  const paginaRef = useRef(null);
  const nomeArquivo = `RQ-1.14-PLANO-DE-COMUNICACAO-REV-${valor(plano?.versao)}`;
  const comunicacoes = Array.isArray(plano?.comunicacoes) ? plano.comunicacoes : [];
  useNomeArquivoPdf(nomeArquivo);

  if (!plano) return null;

  return (
    <div className="relatorio-plano-comunicacao-print">
      <div className="acoes-relatorio-plano-comunicacao">
        <button type="button" onClick={onVoltar}>Voltar</button>
        <button type="button" className="botao-pdf-plano-comunicacao" onClick={() => imprimirPlanoComunicacao(paginaRef.current, nomeArquivo)}>Salvar como PDF</button>
      </div>
      <main ref={paginaRef} className="pagina-pdf-plano-comunicacao">
        <table className="cabecalho-pdf-plano-comunicacao">
          <tbody>
            <tr>
              <td><LogoRelatorio alt="Logo da empresa" /></td>
              <td><strong>PLANO DE COMUNICAÇÃO</strong><span>Sistema de Gestão da Qualidade</span></td>
              <td><strong>RQ 1.14</strong><span>Revisão {valor(plano.versao)}</span><span>Aprovado em {formatarData(plano.aprovadoEm)}</span></td>
            </tr>
          </tbody>
        </table>

        <section className="objetivo-pdf-plano-comunicacao">
          <strong>OBJETIVO</strong>
          {valor(plano.objetivoGeral)}
        </section>

        <table className="tabela-pdf-plano-comunicacao">
          <colgroup>{LARGURAS_COLUNAS.map((largura, indice) => <col key={indice} style={{ width: largura }} />)}</colgroup>
          <thead>
            <tr>
              <th>O que comunicar</th>
              <th>Objetivo</th>
              <th>Público-alvo</th>
              <th>Meio de comunicação</th>
              <th>Frequência</th>
            </tr>
          </thead>
          <tbody>
            {comunicacoes.map((item, indice) => (
              <tr key={`${item.oQueComunicar || "item"}-${indice}`}>
                <td>{valor(item.oQueComunicar)}</td>
                <td>{valor(item.objetivo)}</td>
                <td>{valor(item.publicoAlvo)}</td>
                <td>{valor(item.meioComunicacao)}</td>
                <td>{valor(item.frequencia)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}
